import { defineStore } from 'pinia'
import { backend_api } from '@/utils/api'

export const usePartnumbersStore = defineStore('partnumbers', {
    state: () => ({
        partnumbers: [],
        loading_list: false,
        loading_detail: false,
        selected: null,
        company_partnumbers: [],
        exact_operations: [],
        workop_data: {}, // Store workop data by partnumber UUID
        co2_config: null // Store CO2 config for selected partnumber's product group
    }),
    actions: {
        resetStore() {
            this.partnumbers = []
            this.loading_list = false
            this.loading_detail = false
            this.selected = null
            this.company_partnumbers = []
            this.exact_operations = []
            this.workop_data = {}
        },
        async fetchPartnumbers(filters = {}) {
            this.loading_list = true
            try {
                // Process filters for API request
                const processedFilters = {};
                
                // Handle item_code filter using __in for server-side filtering
                if (filters.company_item_code) {
                    // Split by commas or spaces to create a list of values
                    const itemCodes = filters.company_item_code.split(/[\s,]+/).filter(code => code.trim());
                    if (itemCodes.length > 0) {
                        processedFilters.item_code__in = itemCodes.join(',');
                    }
                }
                
                // Handle description filter using __in for server-side filtering
                if (filters.description) {
                    // Split by commas or spaces to create a list of values
                    const descriptions = filters.description.split(/[\s,]+/).filter(desc => desc.trim());
                    if (descriptions.length > 0) {
                        processedFilters.description__in = descriptions.join(',');
                    }
                }
                
                // Handle OEM filter (convert array of objects to comma-separated UUIDs)
                if (Array.isArray(filters.oem) && filters.oem.length > 0) {
                    processedFilters.oem__in = filters.oem.map(item => item.uuid).join(',');
                } else if (filters.oem && filters.oem.uuid) {
                    // Handle single object case
                    processedFilters.oem = filters.oem.uuid;
                }
                
                // Handle Product Group filter (convert array of objects to comma-separated UUIDs)
                if (Array.isArray(filters.product_group) && filters.product_group.length > 0) {
                    processedFilters.product_group__in = filters.product_group.map(item => item.uuid).join(',');
                } else if (filters.product_group && filters.product_group.uuid) {
                    // Handle single object case
                    processedFilters.product_group = filters.product_group.uuid;
                }
                
                // Handle Company filter (extract UUID if it's an object or use directly if it's a string)
                if (filters.company_uuid) {
                    if (typeof filters.company_uuid === 'object') {
                        processedFilters.company_uuid = filters.company_uuid.uuid;
                    } else {
                        processedFilters.company_uuid = filters.company_uuid;
                    }
                }
                
                // Initial request to get first page and total count
                const limit = 250
                const initialParams = {
                    ...processedFilters,
                    limit
                }
                
                // Make initial request to get first page and total count
                const initialResponse = await backend_api.get('partnumbers/', { params: initialParams })
                let allPartnumbers = [...(initialResponse.data.results || [])]
                const totalCount = initialResponse.data.count
                
                // If there are more pages, prepare requests for all remaining pages
                const pageRequests = []
                if (initialResponse.data.next) {
                    // Calculate how many additional pages we need
                    const totalPages = Math.ceil(totalCount / limit)
                    
                    // Create requests for all remaining pages (starting from page 2)
                    for (let page = 2; page <= totalPages; page++) {
                        const pageParams = {
                            ...processedFilters,
                            limit,
                            offset: (page - 1) * limit
                        }
                        pageRequests.push(backend_api.get('partnumbers/', { params: pageParams }))
                    }
                }
                
                // If we have additional pages, fetch them all in parallel
                if (pageRequests.length > 0) {
                    const pageResponses = await Promise.all(pageRequests)
                    
                    // Combine all results
                    pageResponses.forEach(response => {
                        const pagePartnumbers = response.data.results || []
                        allPartnumbers = [...allPartnumbers, ...pagePartnumbers]
                    })
                }
                
                console.log(`Fetched ${allPartnumbers.length} partnumbers out of ${totalCount} total`)
                
                // Assign the partnumbers directly without workop data
                this.partnumbers = allPartnumbers
            } catch (error) {
                console.error('Error fetching partnumbers:', error);
            } finally {
                this.loading_list = false;
            }
        },
        
        async fetchPartnumberDetail(uuid) {
            this.loading_detail = true
            try {
                // Make all initial requests in parallel
                const [
                    partnumberResponse,
                    companyPartnumbersResponse,
                    operationsResponse
                ] = await Promise.all([
                    backend_api.get(`partnumbers/${uuid}/`),
                    backend_api.get(`company-partnumbers/?partnumber=${uuid}`),
                    backend_api.get(`exact-partnumber-operations/?partnumber=${uuid}`)
                ])
                
                // Process partnumber data
                this.selected = partnumberResponse.data
                
                // Process company partnumbers
                const company_partnumbers = companyPartnumbersResponse.data.results || []
                
                // Process operations
                this.exact_operations = operationsResponse.data.results || []
                
                // Fetch company details for each company partnumber in parallel
                if (company_partnumbers.length > 0) {
                    const companyDetailsPromises = company_partnumbers.map(cp => 
                        backend_api.get(`companies/${cp.company}/`)
                            .then(response => ({
                                ...cp,
                                company_name: response.data.name
                            }))
                            .catch(error => {
                                console.error(`Error fetching company details for ${cp.company}:`, error)
                                return {
                                    ...cp,
                                    company_name: 'Unknown'
                                }
                            })
                    )
                    
                    this.company_partnumbers = await Promise.all(companyDetailsPromises)
                } else {
                    this.company_partnumbers = []
                }
                
                // Fetch CO2 config for the partnumber's product group
                if (this.selected && this.selected.product_group_name) {
                    try {
                        const co2Response = await backend_api.get('product-group-co2-configs/', {
                            params: { name: this.selected.product_group_name }
                        })
                        const configs = co2Response.data.results || co2Response.data
                        this.co2_config = configs.length > 0 ? configs[0] : null
                    } catch (error) {
                        console.error('Error fetching CO2 config:', error)
                        this.co2_config = null
                    }
                } else {
                    this.co2_config = null
                }
            } catch (error) {
                console.error('Error fetching partnumber detail:', error)
            } finally {
                this.loading_detail = false
            }
        },
        
        clearSelected() {
            this.selected = null
            this.company_partnumbers = []
            this.exact_operations = []
            this.workop_data = {}
            this.co2_config = null
        }
    }
})
